/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import camelCase from 'lodash/camelCase';
import { useMemo } from 'react';
import seedrandom from 'seedrandom';

import Image, { ImageProps } from './Image';
import Initials from './Initials';

import styles from '../../styles/partials/author-image.module.css';

export interface AuthorImageProps extends ImageProps {
    slug?: string | null;
    media?: Image | null;
    defaultMedias?: Image[];
    replaceMedias?: Image[];
    size?: string;
    name?: string | null;
    withoutDefault?: boolean;
    withoutCircle?: boolean;
    withPlaceholder?: boolean;
    className?: string | null;
    pictureClassName?: string | null;
    initialsClassName?: string | null;
}

function AuthorImage({
    slug = null,
    media = null,
    defaultMedias = [],
    replaceMedias = [],
    name = null,
    withoutDefault = false,
    withoutCircle = false,
    withPlaceholder = false,
    className = null,
    pictureClassName = null,
    initialsClassName = null,
    ...props
}: AuthorImageProps) {
    const { width, height } = media || {};
    const finalMedia = width !== 714 || height !== 706 ? media : null;
    if (withoutDefault && finalMedia === null) {
        return null;
    }

    const defaultMedia = useMemo(
        () =>
            !withPlaceholder
                ? defaultMedias[Math.floor(seedrandom(slug).double() * defaultMedias.length)]
                : null,
        [withPlaceholder, defaultMedias, slug],
    );

    const replacedMedia = useMemo(
        () => replaceMedias.find(({ id }) => id === camelCase(slug)) || null,
        [replaceMedias, slug],
    );

    return finalMedia === null && !withPlaceholder ? (
        <Initials
            name={name}
            className={classNames([
                styles.container,
                {
                    [styles.withoutCircle]: withoutCircle,
                },
                className,
            ])}
            initialsClassName={initialsClassName}
        />
    ) : (
        <Image
            media={replacedMedia || finalMedia || defaultMedia}
            loading="lazy"
            withPlaceholder={withPlaceholder}
            className={classNames([
                styles.container,
                {
                    [styles.withoutCircle]:
                        withoutCircle || replacedMedia !== null || finalMedia === null,
                },
                className,
            ])}
            pictureClassName={classNames([styles.picture, pictureClassName])}
            {...props}
        />
    );
}

export default AuthorImage;
